import React, { useState } from "react";
import { useSelector } from "react-redux";

import { cn } from "../../../common/u1/cn";
import { Modal } from "../../../common/Modal";
import { GetTheCompany } from "../../../common/GetTheCompany";
import { CreditNotice } from "../../../common/CreditNotice";
import { isSecondaryOrListed, getDetailsCopy, GET_DETAILS_COPY } from "./profileAccess";
import { useGetTheBusiness } from "./useGetTheBusiness";
import { useSpendCredit } from "./useSpendCredit";
import { PurchaseSuccess } from "../../../common/PurchaseSuccess";

/* The "Get The Business" offer, shown over a gated tab. */
/* Two shapes, both live. "modal" — tabs 02-04. The shared Modal, which blurs the whole page behind it (Tabs.jsx:781). "inline" — tab 01. The live screen does... */
export const GetTheBusinessGate = ({
  company,
  trevexId,
  onClose,
  isFollowing = false,
  variant = "modal",
  flow,
  onPurchased,
  /* Who draws the shared flow's dialogs. */
  ownsFlowDialogs = true,
}) => {
  const { userInfo } = useSelector((state) => state.auth);
  const [selectedEnterprise, setSelectedEnterprise] = useState();
  /* Transcribed: the live handler closes the credit notice and opens the
     "purchased" confirmation. */
  const [purchased, setPurchased] = useState(false);
  /* ONE flow instance, shared with the page. */
  const ownFlow = useGetTheBusiness({ company, trevexId });
  const {
    formData,
    setFormData,
    isContinue,
    setIsContinue,
    creditNoticePopup,
    setCreditNoticePopup,
    getCompanyPopup,
    setGetCompanyPopup,
    subscriptionActive,
    userEnterprise,
    canSpendCredit,
    handleGetCompany,
    createWorldBoxOrder,
    secondayListedHandler,
    handlePurchased,
  } = flow || ownFlow;

  const credit = useSpendCredit({
    company,
    trevexId,
    formData,
    onDone: () => {
      setCreditNoticePopup(false);
      setPurchased(true);
      /* The credit is spent and the purchase row exists, but the page's purchase read happened before it. */
      onPurchased?.();
    },
  });

  /* Transcribed: the spend ends on the "purchased" confirmation, which is what
     closes the whole flow. */
  if (ownsFlowDialogs && purchased) {
    return (
      <Modal modalHandle={onClose}>
        <PurchaseSuccess
          onclick={onClose}
          title="Business purchased"
          des="The full profile is now yours to open."
          button="continue"
        />
      </Modal>
    );
  }

  /* The SECOND step, transcribed from BannerCompanyCard:1941-1962. */
  if (ownsFlowDialogs && getCompanyPopup) {
    return (
      <Modal modalHandle={() => setGetCompanyPopup(false)}>
        <GetTheCompany
          company={company}
          company_id={company?.companyID}
          fromBanner
          SubscriptionActive={canSpendCredit}
          handleGetCompany={handleGetCompany}
          createWorldBoxOrder={createWorldBoxOrder}
          handlePurchased={handlePurchased}
          /* Transcribed: this step always asks, and always says the request is
             sourced from secondary sources. */
          acknowledgement
          des={GET_DETAILS_COPY.secondary}
          isContinue={isContinue}
          setIsContinue={setIsContinue}
          secondayListedHandler={secondayListedHandler}
          formData={formData}
          setFormData={setFormData}
          button={"Continue"}
        />
      </Modal>
    );
  }

  /* Transcribed: the credit notice replaces the offer once it opens, with the
     live wording — which changes on whether a credit is actually left. */
  if (ownsFlowDialogs && creditNoticePopup) {
    const views = subscriptionActive?.no_of_profile_views;
    const hasCredit = views >= 1;

    return (
      <Modal modalHandle={() => setCreditNoticePopup(false)}>
        <CreditNotice
          onclick={() => setCreditNoticePopup(false)}
          company_id={company?.companyID}
          NoCredits={!subscriptionActive || views === 0}
          title={hasCredit ? "Attention!!" : "Oopss..."}
          des={
            hasCredit
              ? userEnterprise?.length > 0
                ? `1 credit out of ${views} credit(s)`
                : `You are about to use <b> 1 credit </b> for these request. You will be left with <b> ${
                    views - 1
                  } more credit of this month </b>`
              : "Get this business with your subscription!"
          }
          des2={`Currently you have <b> ${views} Credit </b>. Upgrade your plan to get more credit or else continue via payment method`}
          leftButton={hasCredit ? "Cancel" : "Buy Credits"}
          rightButton={"Process"}
          userInfo={userInfo}
          userEnterprise={userEnterprise}
          selectedEnterprise={selectedEnterprise}
          setSelectedEnterprise={setSelectedEnterprise}
          /* Process spends the credit — transcribed in useSpendCredit. */
          handlePurchased={(type) =>
            credit.spend(type, { selectedEnterprise, isFollowing })
          }
        />
      </Modal>
    );
  }

  const inline = variant === "inline";

  const offer = (
      <GetTheCompany
        company={company}
        company_id={company?.companyID}
        /* Transcribed: the tab-01 card passes neither `fromBanner` nor the
           acknowledgement form. */
        fromBanner={!inline}
        /* Transcribed prop name and value: the live call passes the boolean,
           not the subscription object. */
        SubscriptionActive={canSpendCredit}
        handleGetCompany={handleGetCompany}
        createWorldBoxOrder={createWorldBoxOrder}
        /* Transcribed: tab 01's card orders directly; the gated tabs' modal
           opens the credit notice instead. */
        handlePurchased={inline ? createWorldBoxOrder : handlePurchased}
        /* Transcribed: tab 01 shows neither the acknowledgement form nor the
           secondary-source wording. */
        acknowledgement={inline ? false : isSecondaryOrListed(company)}
        des={inline ? GET_DETAILS_COPY.standard : getDetailsCopy(company)}
        isContinue={isContinue}
        setIsContinue={setIsContinue}
        secondayListedHandler={secondayListedHandler}
        formData={formData}
        setFormData={setFormData}
        button={"Get Details"}
      />
  );

  if (inline) {
    /* The live card: absolutely centred over the blurred content, 450px wide,
       its own white panel. Not a modal — the page behind it still scrolls. */
    return (
      <div
        className={cn(
          "absolute inset-0 z-[1] flex items-start justify-center overflow-y-auto p-4",
          "sm:items-center"
        )}
      >
        <div
          className={cn(
            "my-auto w-full max-w-[450px] rounded-[14px] border border-[var(--line)]",
            "bg-white p-4 shadow-[shadow:var(--menu-shadow)]"
          )}
        >
          {offer}
        </div>
      </div>
    );
  }

  return <Modal modalHandle={onClose}>{offer}</Modal>;
};

export default GetTheBusinessGate;
